import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon, Badge, Box, Button, Center, CopyButton, Group, Loader, Modal, Select, Stack, Table, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { IconBuilding, IconCheck, IconCopy, IconSearch } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { api, bearer } from '../../api/client';
import { logEvent } from '../../lib/telemetry';
import { InlineNotice } from '../../components/InlineNotice';
import { StatusBadge } from '../../components/StatusBadge';
import { canEditEntitlements, canDeleteOrganisation, canManageSso } from '../../staff/staffAccess';
import { catalogueProducts } from '../../staff/catalogueProducts';
import {
  capitalise, describeSsoCapability, expiryDisplay, expiryError, expiryToIso, filterOrgs, grantItemId, grantKey,
  grantableItemIds, itemLabel, offerableItemIds, NO_CAPABILITIES,
  type OrgSummary, type AdminProduct, type AdminProducts, type AdminGrant, type AdminCapabilities,
} from '../../staff/orgEntitlements';

type OrgsState =
  | { kind: 'loading' }
  | { kind: 'ready'; orgs: OrgSummary[] }
  | { kind: 'error'; status: number };

type DetailState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  // grantable: string[] per product; null marks a failed catalogue read (distinct from
  // "genuinely no tiers/modules yet") so the section can say so instead of an empty picker.
  | { kind: 'ready'; products: AdminProducts; grantable: Record<string, string[] | null>; capabilities: AdminCapabilities }
  | { kind: 'error'; status: number };

type PendingRevoke = { productId: string; grant: AdminGrant };
type PendingCapability = { org: OrgSummary; enabled: boolean; hasLivePaidGrant: boolean };
type PendingRoleBasedAccess = { org: OrgSummary; productId: string; productName: string; enabled: boolean };

const ORGS_FAILED = 'cloudhouse.enterprise.organisations.list_failed';
const ORGS_OPENED = 'cloudhouse.enterprise.organisations.opened';
const DETAIL_FAILED = 'cloudhouse.enterprise.organisations.detail_failed';
const CATALOGUE_FAILED = 'cloudhouse.enterprise.organisations.catalogue_failed';
const GRANT_WRITTEN = 'cloudhouse.enterprise.organisations.grant_written';
const GRANT_REVOKED = 'cloudhouse.enterprise.organisations.grant_revoked';
const WRITE_FAILED = 'cloudhouse.enterprise.organisations.write_failed';
const ORG_DELETED = 'cloudhouse.enterprise.organisations.deleted';
const ORG_DELETE_FAILED = 'cloudhouse.enterprise.organisations.delete_failed';
const SSO_LINK_GENERATED = 'cloudhouse.enterprise.organisations.sso_link_generated';
const SSO_LINK_FAILED = 'cloudhouse.enterprise.organisations.sso_link_failed';
const SSO_CAPABILITY_CHANGED = 'cloudhouse.enterprise.organisations.sso_capability_changed';
const ROLE_BASED_ACCESS_CHANGED = 'cloudhouse.enterprise.organisations.role_based_access_changed';

// The API's protected list is server-configured and may extend beyond this default (EIT-182)
// — this only catches the known default proactively, in the UI, before a click round-trips to
// the 403. The server remains the real authority regardless of what this misses.
const KNOWN_PROTECTED_SLUG = 'cloudhouse';

function ssoLinkFailedMessage(status: number, errorCode: string | undefined): string {
  if (status === 429) return 'Too many SSO setup links requested. Wait a minute and try again.';
  if (errorCode === 'not_entitled') return 'This organisation does not hold a paid SSO grant. Grant the SSO module above first.';
  return `SSO setup link failed (${status || 'network'}). Nothing was changed.`;
}

function deleteBlockedReason(org: OrgSummary, ownOrgId: string | null): string | null {
  if (org.id === ownOrgId) return 'You cannot delete your own organisation.';
  if (org.slug === KNOWN_PROTECTED_SLUG) return 'This organisation is protected and cannot be deleted.';
  return null;
}

// Maps the delete endpoint's documented error bodies to operator-facing text. Irreversible and
// non-retriable on partial failure (see stuck-organisation-delete-reconciliation.md), so a
// half-done delete must be surfaced clearly rather than swallowed into a generic message.
function deleteErrorMessage(status: number, error: string | undefined): string {
  switch (error) {
    case 'confirm_org_id_mismatch':
      return "That doesn't match the organisation's id. Nothing was deleted.";
    case 'org_not_found':
      return 'That organisation no longer exists.';
    case 'own_org':
      return 'You cannot delete your own organisation.';
    case 'org_protected':
      return 'This organisation is protected and cannot be deleted.';
    case 'workos_failed':
      return 'Deleted locally, but removing the WorkOS organisation failed. This needs manual reconciliation — see the stuck-delete runbook.';
    case 'teardown_failed':
      return 'Deleted, but tenant teardown did not trigger. This needs manual reconciliation — see the stuck-delete runbook.';
    case 'audit_unavailable':
      return 'The organisation was deleted, but the audit record failed to write. Record this manually.';
    default:
      return `Delete failed (${status || 'network'}). Nothing was changed.`;
  }
}

function capabilityActionWord(enabled: boolean): 'ENABLE' | 'DISABLE' {
  return enabled ? 'ENABLE' : 'DISABLE';
}

function capabilityModalTitle(enabled: boolean): string {
  return enabled ? 'Enable single sign-on?' : 'Disable single sign-on?';
}

function livePaidGrantLine(hasLivePaidGrant: boolean): string {
  return hasLivePaidGrant ? 'This organisation has a live paid grant.' : 'This organisation has no live paid grant.';
}

function roleBasedAccessModalTitle(p: PendingRoleBasedAccess): string {
  return p.enabled ? `Enable role-based access for ${p.productName}?` : `Disable role-based access for ${p.productName}?`;
}

function roleBasedAccessModalBody(p: PendingRoleBasedAccess): string {
  return p.enabled
    ? `Members of ${p.org.name} without a ${p.productName} role see Request access instead of Open, and ${p.productName} is told to check roles. Only do this once ${p.productName} has moved this organisation to role-based access.`
    : `Every member of ${p.org.name} with access to ${p.productName} can open it again, whatever their role. Open requests are kept, and shown again if role-based access is enabled later.`;
}

export function StaffOrganisations() {
  const auth = useAuth();
  const writable = canEditEntitlements(auth.permissions);
  const deletable = canDeleteOrganisation(auth.permissions);
  const ssoWritable = canManageSso(auth.permissions);
  const [ssoLink, setSsoLink] = useState<string | null>(null);
  const [orgsState, setOrgsState] = useState<OrgsState>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<OrgSummary | null>(null);
  const [detail, setDetail] = useState<DetailState>({ kind: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(null);
  const [pendingCapability, setPendingCapability] = useState<PendingCapability | null>(null);
  const [capabilityConfirmText, setCapabilityConfirmText] = useState('');
  const [pendingRoleBasedAccess, setPendingRoleBasedAccess] = useState<PendingRoleBasedAccess | null>(null);
  const [rolesConfirmText, setRolesConfirmText] = useState('');
  const [pendingDelete, setPendingDelete] = useState<OrgSummary | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  // Writes capture the org they were fired for; feedback is skipped if the operator has since
  // moved on, so org A's notice (and refetch) never lands on org B's panel.
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const token = await auth.getAccessToken();
        if (!token) { setOrgsState({ kind: 'error', status: 0 }); return; }
        const { data, error, response } = await api.GET('/admin/organisations', {
          headers: bearer(token), signal: ctrl.signal,
        });
        if (error || !data) {
          logEvent(ORGS_FAILED, 'Staff organisations list failed', { status: response?.status ?? 0 }, 'WARN');
          setOrgsState({ kind: 'error', status: response?.status ?? 0 });
          return;
        }
        const { organisations } = data;
        setOrgsState({ kind: 'ready', orgs: organisations });
        logEvent(ORGS_OPENED, 'Staff opened the organisations page', { organisation_count: organisations.length });
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setOrgsState({ kind: 'error', status: 0 });
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token read once on mount
  }, []);

  useEffect(() => {
    if (!selected) return;
    const ctrl = new AbortController();
    void (async () => {
      setDetail({ kind: 'loading' });
      try {
        const token = await auth.getAccessToken();
        if (!token) { setDetail({ kind: 'error', status: 0 }); return; }
        const { data, error, response } = await api.GET('/admin/organisations/{orgId}/entitlements', {
          params: { path: { orgId: selected.id } },
          headers: bearer(token), signal: ctrl.signal,
        });
        if (error || !data) {
          logEvent(DETAIL_FAILED, 'Staff org entitlements load failed',
            { org_id: selected.id, status: response?.status ?? 0 }, 'WARN');
          setDetail({ kind: 'error', status: response?.status ?? 0 });
          return;
        }
        const { products, capabilities } = data as { products: AdminProducts; capabilities?: AdminCapabilities };

        // The assign picker needs each product's current tiers/modules from the catalogue.
        // Each read degrades to null on its own — one product's failure (including a rejected
        // fetch) must not collapse the entitlements view that already loaded.
        const grantable: Record<string, string[] | null> = {};
        await Promise.all(Object.keys(products).map(async (productId) => {
          try {
            const res = await api.GET('/admin/catalogue/{productId}', {
              params: { path: { productId } }, headers: bearer(token), signal: ctrl.signal,
            });
            if (res.data) {
              grantable[productId] = grantableItemIds(res.data.items);
            } else {
              logEvent(CATALOGUE_FAILED, 'Staff org page catalogue read failed',
                { product_id: productId, status: res.response?.status ?? 0 }, 'WARN');
              grantable[productId] = null;
            }
          } catch (e) {
            if ((e as Error).name === 'AbortError') throw e;
            logEvent(CATALOGUE_FAILED, 'Staff org page catalogue read failed',
              { product_id: productId, status: 0 }, 'WARN');
            grantable[productId] = null;
          }
        }));

        setDetail({ kind: 'ready', products, grantable, capabilities: capabilities ?? NO_CAPABILITIES });
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setDetail({ kind: 'error', status: 0 });
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on selection / write
  }, [selected, reloadKey]);

  async function writeGrant(productId: string, itemId: string, expiresAt: string) {
    const org = selected;
    if (!org) return;
    setBusy(true);
    setNotice(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const body: { basis: string; expiresAt?: string } = { basis: 'paid' };
      const iso = expiryToIso(expiresAt);
      if (iso) body.expiresAt = iso;
      const { error, response } = await api.PUT('/admin/organisations/{orgId}/entitlements/{productId}/grants/{itemId}', {
        params: { path: { orgId: org.id, productId, itemId } },
        headers: bearer(token),
        body,
      });
      if (error) {
        logEvent(WRITE_FAILED, 'Staff grant write failed',
          { org_id: org.id, product_id: productId, item_id: itemId, status: response?.status ?? 0 }, 'WARN');
        if (selectedRef.current?.id !== org.id) return;
        setNotice({ tone: 'error', message: `Grant failed (${response?.status ?? 'network'}). Nothing was changed.` });
        return;
      }
      logEvent(GRANT_WRITTEN, 'Staff granted an entitlement',
        { org_id: org.id, product_id: productId, item_id: itemId });
      if (selectedRef.current?.id !== org.id) return;
      setNotice({ tone: 'success', message: `${itemLabel(itemId)} granted to ${org.name}.` });
      setReloadKey((k) => k + 1);
    } catch {
      logEvent(WRITE_FAILED, 'Staff grant write failed',
        { org_id: org.id, product_id: productId, item_id: itemId, status: 0 }, 'WARN');
      if (selectedRef.current?.id === org.id) {
        setNotice({ tone: 'error', message: 'Grant failed (network). Nothing was changed.' });
      }
    } finally {
      setBusy(false);
    }
  }

  async function generateSsoLink() {
    const org = selected;
    if (!org) return;
    setBusy(true);
    setNotice(null);
    setSsoLink(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const { data, error, response } = await api.POST('/admin/organisations/{orgId}/sso/portal-link', {
        params: { path: { orgId: org.id } },
        headers: bearer(token),
        body: { intent: 'sso' },
      });
      if (error || !data?.link) {
        const status = response?.status ?? 0;
        logEvent(SSO_LINK_FAILED, 'Staff SSO setup link failed', { org_id: org.id, status }, 'WARN');
        if (selectedRef.current?.id !== org.id) return;
        setNotice({ tone: 'error', message: ssoLinkFailedMessage(status, error?.error) });
        return;
      }
      logEvent(SSO_LINK_GENERATED, 'Staff generated an SSO setup link', { org_id: org.id });
      if (selectedRef.current?.id !== org.id) return;
      setSsoLink(data.link);
    } catch {
      logEvent(SSO_LINK_FAILED, 'Staff SSO setup link failed', { org_id: org.id, status: 0 }, 'WARN');
      if (selectedRef.current?.id === org.id) {
        setNotice({ tone: 'error', message: 'SSO setup link failed (network). Nothing was changed.' });
      }
    } finally {
      setBusy(false);
    }
  }

  // Mirrors revokeGrant's shape: the modal stays open (button spinning) until the PUT settles,
  // closed in the finally so a slow write shows as in-flight rather than a silently unchanged row.
  async function setSsoCapability(org: OrgSummary, enabled: boolean) {
    setBusy(true);
    setNotice(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const { data, error, response } = await api.PUT('/admin/organisations/{orgId}/capabilities/sso', {
        params: { path: { orgId: org.id } },
        body: { enabled },
        headers: bearer(token),
      });
      if (error || !data) {
        logEvent(WRITE_FAILED, 'Staff SSO capability change failed', { org_id: org.id, status: response?.status ?? 0 }, 'WARN');
        if (selectedRef.current?.id !== org.id) return;
        setNotice({ tone: 'error', message: `Capability change failed (${response?.status ?? 'network'}). Nothing was changed.` });
        return;
      }
      logEvent(SSO_CAPABILITY_CHANGED, 'Staff changed an SSO capability', { org_id: org.id, enabled });
      if (selectedRef.current?.id !== org.id) return;
      setNotice({ tone: 'success', message: `Single sign-on ${enabled ? 'enabled' : 'disabled'} for ${org.name}.` });
      setReloadKey((k) => k + 1);
    } catch {
      logEvent(WRITE_FAILED, 'Staff SSO capability change failed', { org_id: org.id, status: 0 }, 'WARN');
      if (selectedRef.current?.id === org.id) {
        setNotice({ tone: 'error', message: 'Capability change failed (network). Nothing was changed.' });
      }
    } finally {
      setBusy(false);
      setPendingCapability(null);
    }
  }

  async function setRoleBasedAccess({ org, productId, productName, enabled }: PendingRoleBasedAccess) {
    setBusy(true);
    setNotice(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const { data, error, response } = await api.PUT('/admin/organisations/{orgId}/capabilities/role-based-access', {
        params: { path: { orgId: org.id } },
        body: { productId, enabled },
        headers: bearer(token),
      });
      if (error || !data) {
        logEvent(WRITE_FAILED, 'Staff role-based-access change failed', { org_id: org.id, product_id: productId, status: response?.status ?? 0 }, 'WARN');
        if (selectedRef.current?.id !== org.id) return;
        setNotice({ tone: 'error', message: `Capability change failed (${response?.status ?? 'network'}). Nothing was changed.` });
        return;
      }
      logEvent(ROLE_BASED_ACCESS_CHANGED, 'Staff changed the role-based-access switch', { org_id: org.id, product_id: productId, enabled });
      if (selectedRef.current?.id !== org.id) return;
      setNotice({ tone: 'success', message: enabled
        ? `Role-based access enabled for ${productName} in ${org.name}.`
        : `Role-based access disabled for ${productName} in ${org.name}.` });
      setReloadKey((k) => k + 1);
    } catch {
      logEvent(WRITE_FAILED, 'Staff role-based-access change failed', { org_id: org.id, product_id: productId, status: 0 }, 'WARN');
      if (selectedRef.current?.id === org.id) {
        setNotice({ tone: 'error', message: 'Capability change failed (network). Nothing was changed.' });
      }
    } finally {
      setBusy(false);
      setPendingRoleBasedAccess(null);
    }
  }

  // The modal stays open (button spinning) until the DELETE settles — it is closed in the
  // finally, so a slow write shows as in-flight rather than a silently unchanged table.
  async function revokeGrant({ productId, grant }: PendingRevoke) {
    const org = selected;
    if (!org) return;
    setBusy(true);
    setNotice(null);
    const itemId = grantItemId(grant);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const { error, response } = await api.DELETE('/admin/organisations/{orgId}/entitlements/{productId}/grants/{itemId}', {
        params: { path: { orgId: org.id, productId, itemId } },
        headers: bearer(token),
      });
      if (error) {
        logEvent(WRITE_FAILED, 'Staff grant revoke failed',
          { org_id: org.id, product_id: productId, item_id: itemId, status: response?.status ?? 0 }, 'WARN');
        if (selectedRef.current?.id !== org.id) return;
        setNotice({ tone: 'error', message: `Revoke failed (${response?.status ?? 'network'}). Nothing was changed.` });
        return;
      }
      logEvent(GRANT_REVOKED, 'Staff revoked an entitlement',
        { org_id: org.id, product_id: productId, item_id: itemId });
      if (selectedRef.current?.id !== org.id) return;
      setNotice({ tone: 'success', message: `${itemLabel(itemId)} revoked from ${org.name}.` });
      setReloadKey((k) => k + 1);
    } catch {
      logEvent(WRITE_FAILED, 'Staff grant revoke failed',
        { org_id: org.id, product_id: productId, item_id: itemId, status: 0 }, 'WARN');
      if (selectedRef.current?.id === org.id) {
        setNotice({ tone: 'error', message: 'Revoke failed (network). Nothing was changed.' });
      }
    } finally {
      setBusy(false);
      setPendingRevoke(null);
    }
  }

  // Irreversible and non-retriable on partial failure — see stuck-organisation-delete-
  // reconciliation.md. An id mismatch or org-not-found leaves the modal open so the operator
  // can correct it; every other outcome closes the modal, since the server has already committed
  // (or definitively failed) the action and retrying would not help.
  async function deleteOrganisation(org: OrgSummary) {
    setDeleteBusy(true);
    setNotice(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setNotice({ tone: 'error', message: 'Your session has expired. Refresh to sign in again; nothing was changed.' });
        return;
      }
      const { error, response } = await api.DELETE('/admin/organisations/{orgId}', {
        params: { path: { orgId: org.id }, query: { confirm_org_id: deleteConfirmText.trim() } },
        headers: bearer(token),
      });
      if (error) {
        const status = response?.status ?? 0;
        const body = error as { error?: string } | undefined;
        logEvent(ORG_DELETE_FAILED, 'Staff organisation delete failed',
          { org_id: org.id, status, reason: body?.error }, 'WARN');
        setNotice({ tone: 'error', message: deleteErrorMessage(status, body?.error) });
        if (body?.error === 'confirm_org_id_mismatch' || body?.error === 'org_not_found') return;
        setPendingDelete(null);
        return;
      }
      logEvent(ORG_DELETED, 'Staff deleted an organisation', { org_id: org.id });
      setOrgsState((s) => (s.kind === 'ready' ? { kind: 'ready', orgs: s.orgs.filter((o) => o.id !== org.id) } : s));
      if (selectedRef.current?.id === org.id) { setSelected(null); setDetail({ kind: 'idle' }); }
      setNotice({ tone: 'success', message: `${org.name} was deleted.` });
      setPendingDelete(null);
    } catch {
      logEvent(ORG_DELETE_FAILED, 'Staff organisation delete failed', { org_id: org.id, status: 0 }, 'WARN');
      setNotice({ tone: 'error', message: 'Delete failed (network). Nothing was changed.' });
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Box p={26} flex={1} style={{ overflowY: 'auto' }}>
      <Stack gap={4} mb={20}>
        <Text fz={22} fw={700}>Organisations</Text>
        <Text fz="sm" c="var(--text-muted)">Manage organisation grants</Text>
      </Stack>

      {notice && <Box mb={14}><InlineNotice tone={notice.tone} message={notice.message} onClose={() => setNotice(null)} /></Box>}

      <Group align="flex-start" gap={20} wrap="nowrap">
        {/* Organisation picker */}
        <Stack gap={0} w={280} style={{ flexShrink: 0, border: '1px solid var(--border-default)', borderRadius: 12 }}
          bg="var(--bg-surface)" data-testid="org-list">
          <Box p={12} style={{ borderBottom: '1px solid var(--border-default)' }}>
            <TextInput
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Search organisations…"
              leftSection={<IconSearch size={14} />}
              aria-label="Search organisations"
            />
          </Box>
          {orgsState.kind === 'loading' && <Center p="lg" role="progressbar" aria-label="Loading organisations"><Loader size="sm" /></Center>}
          {orgsState.kind === 'error' && (
            <Text p="md" fz="sm" c="var(--text-muted)">Couldn't load organisations. Refresh to retry.</Text>
          )}
          {orgsState.kind === 'ready' && filterOrgs(orgsState.orgs, query).length === 0 && (
            <Text p="md" fz="sm" c="var(--text-subtle)">
              {query.trim() ? `No organisations match "${query.trim()}".` : 'No organisations yet.'}
            </Text>
          )}
          {orgsState.kind === 'ready' && filterOrgs(orgsState.orgs, query).map((org) => (
            <UnstyledButton
              key={org.id}
              onClick={() => {
                setSelected(org);
                // Page-level feedback belongs to the previous org; never show it over this one.
                setNotice(null);
                setPendingRevoke(null);
                setPendingCapability(null);
                setPendingRoleBasedAccess(null);
                setSsoLink(null);
              }}
              p="11px 14px"
              data-selected={selected?.id === org.id || undefined}
              style={{
                borderBottom: '1px solid var(--border-default)',
                background: selected?.id === org.id ? 'var(--bg-subtle)' : undefined,
                boxShadow: selected?.id === org.id ? 'inset 3px 0 0 var(--accent-text)' : undefined,
              }}
            >
              <Group gap={6} wrap="nowrap">
                <Text fz="sm" fw={600}>{org.name}</Text>
                {org.slug === null && (
                  <Badge size="xs" variant="light" color="gray">Not in our records</Badge>
                )}
              </Group>
            </UnstyledButton>
          ))}
        </Stack>

        {/* Selected organisation */}
        <Box flex={1} miw={0}>
          {!selected && (
            <Center bg="var(--bg-surface)" mih={260}
              style={{ border: '1px solid var(--border-default)', borderRadius: 12 }}>
              <Stack align="center" gap={4}>
                <IconBuilding size={26} color="var(--text-subtle)" stroke={1.5} />
                <Text fw={600} fz="sm">No organisation selected</Text>
                <Text fz="sm" c="var(--text-subtle)">Select an organisation to see more details</Text>
              </Stack>
            </Center>
          )}
          {selected && detail.kind === 'loading' && (
            <Center p="xl" role="progressbar" aria-label="Loading entitlements"><Loader /></Center>
          )}
          {selected && detail.kind === 'error' && (
            <Stack gap={10} align="flex-start">
              <InlineNotice tone="error" message={`Couldn't load ${selected.name}'s entitlements (${detail.status || 'network'}).`} />
              <Button variant="default" onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>
            </Stack>
          )}
          {selected && detail.kind === 'ready' && (
            <Stack gap={0} bg="var(--bg-surface)"
              style={{ border: '1px solid var(--border-default)', borderRadius: 12 }} data-testid="org-detail">
              <Group p="16px 18px" gap={8} justify="space-between" style={{ borderBottom: '1px solid var(--border-default)' }}>
                <Group gap={8}>
                  <Text fw={700}>{selected.name}</Text>
                  <Text fz={11} c="var(--text-subtle)" ff="monospace">{selected.id}</Text>
                  {selected.slug === null && (
                    <Badge size="xs" variant="light" color="gray">Not in our records</Badge>
                  )}
                  <CopyButton value={selected.id}>
                    {({ copied, copy }) => (
                      <ActionIcon variant="subtle" color="gray" size="sm" onClick={copy}
                        aria-label="Copy organisation id">
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    )}
                  </CopyButton>
                </Group>
                {deletable && (
                  <Group gap={10}>
                    {deleteBlockedReason(selected, auth.organizationId) && (
                      <Text fz="xs" c="var(--text-subtle)">{deleteBlockedReason(selected, auth.organizationId)}</Text>
                    )}
                    <Button size="compact-sm" fz="xs" variant="default" color="red"
                      disabled={!!deleteBlockedReason(selected, auth.organizationId)}
                      onClick={() => { setDeleteConfirmText(''); setPendingDelete(selected); }}>
                      Delete organisation
                    </Button>
                  </Group>
                )}
              </Group>

              {Object.entries(detail.products).map(([productId, product]) => product && (
                <ProductSection
                  // reloadKey remounts sections after a write, resetting the pickers so a
                  // just-granted item is not left selected with Grant still armed.
                  key={`${productId}-${reloadKey}`}
                  productId={productId}
                  product={product}
                  grantable={detail.grantable[productId] ?? null}
                  writable={writable}
                  busy={busy}
                  onGrant={writeGrant}
                  onRevoke={(grant) => setPendingRevoke({ productId, grant })}
                  onRoleBasedAccess={(enabled) => {
                    setRolesConfirmText('');
                    setPendingRoleBasedAccess({
                      org: selected, productId, enabled,
                      productName: catalogueProducts.find((p) => p.id === productId)?.name ?? productId,
                    });
                  }}
                />
              ))}
              {ssoWritable && (
                <SsoSection
                  link={ssoLink}
                  busy={busy}
                  capabilities={detail.capabilities}
                  onGenerate={() => void generateSsoLink()}
                  onSwitch={(enabled) => {
                    setCapabilityConfirmText('');
                    setPendingCapability({ org: selected, enabled, hasLivePaidGrant: detail.capabilities.hasLivePaidGrant });
                  }}
                />
              )}
            </Stack>
          )}
        </Box>
      </Group>

      <Modal opened={pendingRevoke !== null} onClose={() => { if (!busy) setPendingRevoke(null); }}
        title="Revoke grant?" centered>
        {pendingRevoke && (
          <Stack gap="md">
            <Text fz="sm">
              Revoke <Text component="span" fz="sm" fw={600}>{itemLabel(grantItemId(pendingRevoke.grant))}</Text> from{' '}
              <Text component="span" fz="sm" fw={600}>{selected?.name}</Text>? Access ends on their next page load.
              You can grant it again later.
            </Text>
            <Group justify="flex-end" gap={10}>
              <Button variant="default" disabled={busy} onClick={() => setPendingRevoke(null)}>Cancel</Button>
              <Button color="red" loading={busy} onClick={() => void revokeGrant(pendingRevoke)}>Revoke</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={pendingCapability !== null}
        onClose={() => { if (!busy) setPendingCapability(null); }}
        title={pendingCapability ? capabilityModalTitle(pendingCapability.enabled) : ''} centered>
        {pendingCapability && (
          <Stack gap="md">
            {pendingCapability.enabled ? (
              <>
                <Text fz="sm">
                  The organisation&apos;s admins gain the Single sign-on setup controls. Only do this for
                  a paying organisation or an agreed exception.
                </Text>
                <Text fz="sm" fw={600}>
                  {livePaidGrantLine(pendingCapability.hasLivePaidGrant)}
                </Text>
              </>
            ) : (
              <Text fz="sm">
                Users keep any existing connection until it is removed; the organisation&apos;s admins
                lose the setup controls.
              </Text>
            )}
            <Text fz="sm" c="var(--text-subtle)">
              To confirm, type {capabilityActionWord(pendingCapability.enabled)}.
            </Text>
            <TextInput
              value={capabilityConfirmText}
              onChange={(e) => setCapabilityConfirmText(e.currentTarget.value)}
              placeholder={`Type ${capabilityActionWord(pendingCapability.enabled)}`}
              aria-label={`Type ${capabilityActionWord(pendingCapability.enabled)} to confirm`}
              disabled={busy}
            />
            <Group justify="flex-end" gap={10}>
              <Button variant="default" disabled={busy} onClick={() => setPendingCapability(null)}>Cancel</Button>
              <Button
                loading={busy}
                disabled={capabilityConfirmText !== capabilityActionWord(pendingCapability.enabled)}
                onClick={() => void setSsoCapability(pendingCapability.org, pendingCapability.enabled)}
              >
                {pendingCapability.enabled ? 'Enable' : 'Disable'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={pendingRoleBasedAccess !== null}
        onClose={() => { if (!busy) setPendingRoleBasedAccess(null); }}
        title={pendingRoleBasedAccess ? roleBasedAccessModalTitle(pendingRoleBasedAccess) : ''} centered>
        {pendingRoleBasedAccess && (
          <Stack gap="md">
            <Text fz="sm">{roleBasedAccessModalBody(pendingRoleBasedAccess)}</Text>
            <Text fz="sm" c="var(--text-subtle)">
              To confirm, type {capabilityActionWord(pendingRoleBasedAccess.enabled)}.
            </Text>
            <TextInput
              value={rolesConfirmText}
              onChange={(e) => setRolesConfirmText(e.currentTarget.value)}
              placeholder={`Type ${capabilityActionWord(pendingRoleBasedAccess.enabled)}`}
              aria-label={`Type ${capabilityActionWord(pendingRoleBasedAccess.enabled)} to confirm`}
              disabled={busy}
            />
            <Group justify="flex-end" gap={10}>
              <Button variant="default" disabled={busy} onClick={() => setPendingRoleBasedAccess(null)}>Cancel</Button>
              <Button
                loading={busy}
                disabled={rolesConfirmText !== capabilityActionWord(pendingRoleBasedAccess.enabled)}
                onClick={() => void setRoleBasedAccess(pendingRoleBasedAccess)}
              >
                {pendingRoleBasedAccess.enabled ? 'Enable' : 'Disable'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={pendingDelete !== null}
        onClose={() => { if (!deleteBusy) { setPendingDelete(null); setDeleteConfirmText(''); } }}
        title="Delete organisation?" centered
        styles={{
          header: { position: 'relative' },
          title: { flex: 1, textAlign: 'center' },
          close: { position: 'absolute', right: 'var(--mantine-spacing-md)' },
        }}>
        {pendingDelete && (
          <Stack gap="md">
            <Text fz="sm" c="var(--status-danger)" fw={600}>
              This cannot be undone.
            </Text>
            <Text fz="sm">
              The WorkOS organisation and its whole tenant for{' '}
              <Text component="span" fz="sm" fw={600}>{pendingDelete.name}</Text> will be
              deleted. To confirm, type the organisation&apos;s id:{' '}
              <Text component="span" fz="sm" fw={600} ff="monospace">{pendingDelete.id}</Text>
            </Text>
            <TextInput
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.currentTarget.value)}
              placeholder="Organisation id"
              aria-label="Type the organisation's id to confirm deletion"
              disabled={deleteBusy}
            />
            <Group justify="flex-end" gap={10}>
              <Button variant="default" disabled={deleteBusy}
                onClick={() => { setPendingDelete(null); setDeleteConfirmText(''); }}>
                Cancel
              </Button>
              <Button color="red" loading={deleteBusy}
                disabled={deleteConfirmText.trim() !== pendingDelete.id}
                onClick={() => void deleteOrganisation(pendingDelete)}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}

function ProductSection({ productId, product, grantable, writable, busy, onGrant, onRevoke, onRoleBasedAccess }: Readonly<{
  productId: string;
  product: AdminProduct;
  grantable: string[] | null;
  writable: boolean;
  busy: boolean;
  onGrant: (productId: string, itemId: string, expiresAt: string) => void;
  onRevoke: (grant: AdminGrant) => void;
  onRoleBasedAccess: (enabled: boolean) => void;
}>) {
  const [adding, setAdding] = useState(false);
  const [itemId, setItemId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const grants = product.grants ?? [];
  const now = new Date();
  const expiryProblem = expiryError(expiresAt, now);
  const productName = catalogueProducts.find((p) => p.id === productId)?.name ?? productId;
  // Only items the org doesn't already hold live — a live trial must be revoked, not silently
  // rewritten into a perpetual paid grant by an innocent-looking second Grant.
  const offerable = grantable === null ? null : offerableItemIds(grantable, grants, now);

  return (
    <Stack gap={12} p="16px 18px" style={{ borderBottom: '1px solid var(--border-default)' }}
      data-testid={`org-product-${productId}`}>
      <Group gap={9}>
        <Text fw={700} fz="sm">{productName}</Text>
        {product.status === 'hidden'
          ? <Text fz="sm" c="var(--text-subtle)">Hidden</Text>
          : <StatusBadge variant={product.status} />}
        {product.trialAvailable && <Text fz="xs" c="var(--text-subtle)">Self-serve trial available</Text>}
      </Group>

      {grants.length === 0
        ? <Text fz="sm" c="var(--text-subtle)">No grants yet.</Text>
        : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Grant</Table.Th>
                <Table.Th>Basis</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {grants.map((grant) => {
                const expiry = expiryDisplay(grant.expiresAt, now);
                return (
                <Table.Tr key={grantKey(grant)} c={expiry.lapsed ? 'var(--text-subtle)' : undefined}>
                  <Table.Td fz="sm">
                    {itemLabel(grantItemId(grant))}
                    <Text fz={10.5} c="var(--text-subtle)" ff="monospace">{grantItemId(grant)}</Text>
                  </Table.Td>
                  <Table.Td fz="sm">{capitalise(grant.basis)}</Table.Td>
                  <Table.Td fz="sm">{expiry.label}</Table.Td>
                  <Table.Td>
                    {writable && (
                      <Button size="compact-sm" fz="xs" variant="default" disabled={busy}
                        // Danger tint mirroring ProfileUsers' iconActionStyle; --button-bg, not
                        // the background property, so the :hover repaint works.
                        style={{
                          '--button-color': 'var(--status-danger)',
                          '--button-bd': '1px solid color-mix(in srgb, var(--status-danger), transparent 60%)',
                          '--button-hover': 'color-mix(in srgb, var(--status-danger), transparent 85%)',
                          '--button-bg': 'var(--bg-surface)',
                        }}
                        onClick={() => onRevoke(grant)}>
                        Revoke
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

      {writable && offerable === null && (
        <Text fz="sm" c="var(--status-warning)">
          Catalogue unavailable, so granting is paused. Refresh to retry.
        </Text>
      )}
      {writable && offerable !== null && offerable.length > 0 && !adding && (
        <Box>
          <Button variant="subtle" size="compact-sm" onClick={() => setAdding(true)}>+ Add grant</Button>
        </Box>
      )}
      {writable && offerable !== null && offerable.length > 0 && adding && (
        <Group gap={10} align="flex-start">
          <Select
            data={offerable.map((id) => ({ value: id, label: itemLabel(id) }))}
            value={itemId}
            onChange={setItemId}
            placeholder="Choose a tier or module"
            aria-label={`Item to grant for ${productId}`}
            w={220}
          />
          <TextInput
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.currentTarget.value)}
            placeholder="Expiry (optional)"
            aria-label={`Expiry for ${productId}, optional`}
            error={expiryProblem}
            w={170}
          />
          <Button
            disabled={!itemId || !!expiryProblem || busy}
            onClick={() => { if (itemId) onGrant(productId, itemId, expiresAt); }}
          >
            Grant
          </Button>
          <Button variant="default" onClick={() => { setAdding(false); setItemId(null); setExpiresAt(''); }}>
            Cancel
          </Button>
        </Group>
      )}

      <Stack gap={8} align="flex-start" pt={14} mt={4} style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <Text fw={600} fz="sm">Role-based access</Text>
        <Text fz="sm">
          {product.roleBasedAccess
            ? `Enabled. Only members with a ${productName} role can open it; anyone else can request access from the hub.`
            : `Not enabled. Every member of this organisation can open ${productName} with full admin access.`}
        </Text>
        {writable && (
          <Button variant="default" size="compact-sm" disabled={busy} onClick={() => onRoleBasedAccess(!product.roleBasedAccess)}>
            {product.roleBasedAccess ? 'Disable role-based access' : 'Enable role-based access'}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

// Staff-assisted SSO setup: mint a WorkOS Admin Portal link for the selected org and show it for
// copying into a call or email. The link is single-use and expires in five minutes, so it is shown
// once and never persisted; a fresh one is a click away.
function SsoSection({ link, busy, capabilities, onGenerate, onSwitch }: Readonly<{
  link: string | null;
  busy: boolean;
  capabilities: AdminCapabilities;
  onGenerate: () => void;
  onSwitch: (enabled: boolean) => void;
}>) {
  const enabled = capabilities.sso?.enabled ?? false;
  return (
    <Stack gap={12} p="16px 18px" style={{ borderBottom: '1px solid var(--border-default)' }} data-testid="org-sso">
      <Group gap={9}>
        <Text fw={700} fz="sm">Single sign-on</Text>
      </Group>
      <Group gap={8} align="center">
        <Text fz="sm">{describeSsoCapability(capabilities.sso)}</Text>
        {enabled && !capabilities.hasLivePaidGrant && (
          <Badge color="yellow" variant="light">No live paid grant</Badge>
        )}
      </Group>
      <Box>
        <Button variant="default" size="compact-sm" disabled={busy} onClick={() => onSwitch(!enabled)}>
          {enabled ? 'Disable single sign-on' : 'Enable single sign-on'}
        </Button>
      </Box>
      <Text fz="sm" c="var(--text-subtle)">
        Generate an Admin Portal link for this organisation's IT admin. It expires in five minutes.
      </Text>
      <Group gap={10} align="flex-start">
        <Button
          variant="default" size="compact-sm" disabled={busy || !enabled}
          title={enabled ? undefined : 'Enable single sign-on for this organisation first'}
          onClick={onGenerate}
        >
          Generate SSO setup link
        </Button>
        {link && (
          <>
            <TextInput value={link} readOnly aria-label="SSO setup link" w={380} />
            <CopyButton value={link}>
              {({ copied, copy }) => (
                <ActionIcon variant="default" size="lg" aria-label="Copy SSO setup link" onClick={copy}>
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              )}
            </CopyButton>
          </>
        )}
      </Group>
    </Stack>
  );
}
